import { wrapApiCall } from '@/lib/axios'
import { clampTotalPages, fetchPagesInBatches } from '@/utils/helper/paged-fetch.helper'
import type { AxiosInstance } from 'axios'

const base = '/postgrest'
const POSTGREST_PAGE_SIZE = 500

const CLASS_SUMMARY_SELECT = [
  'booking_id',
  'class_id',
  'class_name',
  'trainer_id',
  'trainer_full_name',
  'member_gender',
  'member_age_years',
  'day_local',
  'booked_count',
  'show_count',
  'no_show_count',
  'cancelled_count',
  'queued_count',
].join(',')

const CLASS_SCHEDULE_FACT_SELECT = [
  'schedule_id',
  'class_id',
  'class_name',
  'capacity',
  'start_time',
  'schedule_cancelled_at',
  'year_local',
  'month_local',
  'day_local',
  'weekday_index',
  'start_hour_local',
  'start_time_local',
  'booked_count',
  'attended_count',
  'no_show_count',
  'cancelled_count',
  'queued_count',
].join(',')

const TOP_CLASSES_BY_BOOKINGS_SELECT = [
  'class_id',
  'class_name',
  'year_local',
  'month_local',
  'schedule_count',
  'attended_count',
  'cancelled_count',
  'total_bookings',
  'capacity_total',
  'fill_rate',
].join(',')

const toNumber = (value: unknown) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const toNullableNumber = (value: unknown) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const toNullableString = (value: unknown) => (typeof value === 'string' ? value : null)

export type DashboardClassBookingFactRow = {
  bookingId: number
  classId: number
  className: string
  trainerId: number | null
  trainerFullName: string | null
  memberGender: string | null
  memberAgeYears: number
  dayLocal: number
  bookedCount: number
  showCount: number
  noShowCount: number
  cancelledCount: number
  queuedCount: number
}

/** One NORMAL class session with capacity + per-status booking counts (QTY-1962..1965). */
export type DashboardClassScheduleFactRow = {
  scheduleId: number
  classId: number
  className: string
  capacity: number
  /** Session start as an ISO timestamptz — compare with "now" for started/future. */
  startTime: string | null
  scheduleCancelledAt: string | null
  /** Bangkok-local calendar date parts of the session start. */
  yearLocal: number
  monthLocal: number
  dayLocal: number
  /** ISO weekday, 1 = Monday .. 7 = Sunday (Bangkok-local). */
  weekdayIndex: number
  /** Bangkok-local start hour 0..23. */
  startHourLocal: number
  /** Bangkok-local "HH:MM" start label. */
  startTimeLocal: string
  bookedCount: number
  attendedCount: number
  noShowCount: number
  cancelledCount: number
  queuedCount: number
}

export type DashboardClassTopBookingsRow = {
  classId: number
  className: string
  yearLocal: number
  monthLocal: number
  scheduleCount: number
  attendedCount: number
  cancelledCount: number
  totalBookings: number
  capacityTotal: number
  fillRate: number
}

export const ClassSummaryService = (client: AxiosInstance) => {
  return {
    async getClassBookingFactRows({
      year,
      month,
    }: {
      year: number
      month: number
    }): Promise<DashboardClassBookingFactRow[]> {
      const mapRow = (row: Record<string, unknown>): DashboardClassBookingFactRow => ({
        bookingId: toNumber(row.booking_id),
        classId: toNumber(row.class_id),
        className: toNullableString(row.class_name) ?? '-',
        trainerId: toNullableNumber(row.trainer_id),
        trainerFullName: toNullableString(row.trainer_full_name),
        memberGender: toNullableString(row.member_gender),
        memberAgeYears: toNumber(row.member_age_years),
        dayLocal: toNumber(row.day_local),
        bookedCount: toNumber(row.booked_count),
        showCount: toNumber(row.show_count),
        noShowCount: toNumber(row.no_show_count),
        cancelledCount: toNumber(row.cancelled_count),
        queuedCount: toNumber(row.queued_count),
      })

      const fetchPage = (page: number, withCount: boolean) => {
        const query = new URLSearchParams()
        query.append('select', CLASS_SUMMARY_SELECT)
        if (withCount) query.append('count', 'true')
        query.append('page', String(page))
        query.append('pageSize', String(POSTGREST_PAGE_SIZE))
        query.append('order', 'class_name.asc,day_local.asc,booking_id.asc')
        query.append('filter', `year_local=eq.${year}`)
        query.append('filter', `month_local=eq.${month}`)

        return wrapApiCall(() =>
          client.get<{
            data?: Record<string, unknown>[]
            pagination?: Record<string, unknown>
          }>(`${base}/dashboard_class_booking_fact_view?${query.toString()}`)
        )
      }

      // Page 1 requests the count so we know the total up front; the
      // remaining pages skip `count=true` (the backend would otherwise
      // re-run the full view just to recompute an unchanging total) and are
      // fetched in parallel instead of sequentially, since the total is
      // already known.
      const { data: firstData } = await fetchPage(1, true)
      const firstRows = firstData?.data ?? []

      const totalRaw = Number(firstData?.pagination?.total ?? firstRows.length)
      const total = Number.isFinite(totalRaw) ? totalRaw : firstRows.length
      const totalPages = clampTotalPages(
        total,
        POSTGREST_PAGE_SIZE,
        'class-summary.getClassBookingFactRows'
      )

      if (totalPages <= 1) {
        return firstRows.map(mapRow)
      }

      const restPages = await fetchPagesInBatches(
        Array.from({ length: totalPages - 1 }, (_, index) => index + 2),
        (page) => fetchPage(page, false).then(({ data }) => data?.data ?? [])
      )

      return [firstRows, ...restPages].flat().map(mapRow)
    },
    async getClassScheduleFactRows({
      year,
      month,
    }: {
      year: number
      month: number
    }): Promise<DashboardClassScheduleFactRow[]> {
      const mapRow = (row: Record<string, unknown>): DashboardClassScheduleFactRow => ({
        scheduleId: toNumber(row.schedule_id),
        classId: toNumber(row.class_id),
        className: toNullableString(row.class_name) ?? '-',
        capacity: toNumber(row.capacity),
        startTime: toNullableString(row.start_time),
        scheduleCancelledAt: toNullableString(row.schedule_cancelled_at),
        yearLocal: toNumber(row.year_local),
        monthLocal: toNumber(row.month_local),
        dayLocal: toNumber(row.day_local),
        weekdayIndex: toNumber(row.weekday_index),
        startHourLocal: toNumber(row.start_hour_local),
        startTimeLocal: toNullableString(row.start_time_local) ?? '',
        bookedCount: toNumber(row.booked_count),
        attendedCount: toNumber(row.attended_count),
        noShowCount: toNumber(row.no_show_count),
        cancelledCount: toNumber(row.cancelled_count),
        queuedCount: toNumber(row.queued_count),
      })

      const fetchPage = (page: number, withCount: boolean) => {
        const query = new URLSearchParams()
        query.append('select', CLASS_SCHEDULE_FACT_SELECT)
        if (withCount) query.append('count', 'true')
        query.append('page', String(page))
        query.append('pageSize', String(POSTGREST_PAGE_SIZE))
        query.append('order', 'class_name.asc,schedule_id.asc')
        query.append('filter', `year_local=eq.${year}`)
        query.append('filter', `month_local=eq.${month}`)

        return wrapApiCall(() =>
          client.get<{
            data?: Record<string, unknown>[]
            pagination?: Record<string, unknown>
          }>(`${base}/dashboard_class_schedule_fact_view?${query.toString()}`)
        )
      }

      // Same paging strategy as getClassBookingFactRows: count only on page 1,
      // then fetch the rest in parallel.
      const { data: firstData } = await fetchPage(1, true)
      const firstRows = firstData?.data ?? []

      const totalRaw = Number(firstData?.pagination?.total ?? firstRows.length)
      const total = Number.isFinite(totalRaw) ? totalRaw : firstRows.length
      const totalPages = clampTotalPages(
        total,
        POSTGREST_PAGE_SIZE,
        'class-summary.getClassScheduleFactRows'
      )

      if (totalPages <= 1) {
        return firstRows.map(mapRow)
      }

      const restPages = await fetchPagesInBatches(
        Array.from({ length: totalPages - 1 }, (_, index) => index + 2),
        (page) => fetchPage(page, false).then(({ data }) => data?.data ?? [])
      )

      return [firstRows, ...restPages].flat().map(mapRow)
    },
    async getTopClassesByBookingsRows({
      year,
      month,
    }: {
      year: number
      month: number
    }): Promise<DashboardClassTopBookingsRow[]> {
      const query = new URLSearchParams()
      query.append('select', TOP_CLASSES_BY_BOOKINGS_SELECT)
      query.append('order', 'total_bookings.desc,class_name.asc')
      query.append('filter', `year_local=eq.${year}`)
      query.append('filter', `month_local=eq.${month}`)

      const { data } = await wrapApiCall(() =>
        client.get<{ data?: Record<string, unknown>[] }>(
          `${base}/dashboard_class_top_bookings_view?${query.toString()}`
        )
      )

      const rows = data?.data ?? []

      return rows.map((row) => ({
        classId: toNumber(row.class_id),
        className: toNullableString(row.class_name) ?? '-',
        yearLocal: toNumber(row.year_local),
        monthLocal: toNumber(row.month_local),
        scheduleCount: toNumber(row.schedule_count),
        attendedCount: toNumber(row.attended_count),
        cancelledCount: toNumber(row.cancelled_count),
        totalBookings: toNumber(row.total_bookings),
        capacityTotal: toNumber(row.capacity_total),
        fillRate: toNumber(row.fill_rate),
      }))
    },
  }
}
